import { Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import { applyHistoricalImport, createHistoricalPreview, loadImportItems } from "../lib/api";
import { parseRubles } from "../lib/money";

interface ImportRow {
  id: string;
  month: string;
  income: string;
  expense: string;
}

interface HistoricalImportViewProps {
  onChanged: () => Promise<void>;
}

export function HistoricalImportView({ onChanged }: HistoricalImportViewProps) {
  const [rows, setRows] = useState<ImportRow[]>([{ id: crypto.randomUUID(), month: "", income: "0", expense: "0" }]);
  const [batch, setBatch] = useState<{ id: string; total_items: number; valid_items: number; status: string } | null>(null);
  const [items, setItems] = useState<Array<Record<string, unknown>>>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function preview(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const payload = rows.map((row) => ({
        month_start: `${row.month}-01`,
        income_total_minor: parseRubles(row.income).toString(),
        expense_total_minor: parseRubles(row.expense).toString(),
        income_sources: [],
        expense_categories: [],
      }));
      const nextBatch = await createHistoricalPreview(payload);
      setBatch(nextBatch);
      setItems(await loadImportItems(nextBatch.id));
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Не удалось проверить импорт");
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!batch) return;
    setBusy(true);
    setMessage(null);
    try {
      await applyHistoricalImport(batch.id);
      setMessage("Исторические месяцы добавлены в сводку. Балансы счетов не изменились.");
      setBatch(null);
      setItems([]);
      await onChanged();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Не удалось применить импорт");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="view is-visible">
      <div className="management-head"><div><p className="panel-label">Перенос старых итогов</p><h2>Исторические данные</h2></div></div>
      <section className="panel import-panel">
        <div className="import-explanation"><h3>Доходы и расходы по месяцам</h3><p>Добавьте итоги только за завершённые месяцы. Они появятся в аналитике, но не изменят остатки карт и счетов. Детализацию можно оставить пустой — она будет показана как «Без детализации».</p></div>
        <form onSubmit={preview}>
          <div className="import-grid import-grid-head"><span>Месяц</span><span>Получено, ₽</span><span>Потрачено, ₽</span><span /></div>
          {rows.map((row) => <div className="import-grid" key={row.id}>
            <input type="month" required value={row.month} onChange={(event) => setRows(rows.map((item) => item.id === row.id ? { ...item, month: event.target.value } : item))} />
            <input inputMode="decimal" required value={row.income} onChange={(event) => setRows(rows.map((item) => item.id === row.id ? { ...item, income: event.target.value } : item))} />
            <input inputMode="decimal" required value={row.expense} onChange={(event) => setRows(rows.map((item) => item.id === row.id ? { ...item, expense: event.target.value } : item))} />
            <button className="icon-action" type="button" disabled={rows.length === 1} onClick={() => setRows(rows.filter((item) => item.id !== row.id))}><Trash2 size={16} /></button>
          </div>)}
          <div className="import-actions"><button className="secondary-button" type="button" onClick={() => setRows([...rows, { id: crypto.randomUUID(), month: "", income: "0", expense: "0" }])}><Plus size={16} />Добавить месяц</button><button className="confirm-primary" disabled={busy} type="submit">{busy ? "Проверяю…" : "Проверить перед импортом"}</button></div>
        </form>
        {message && <p className="form-message import-message">{message}</p>}
      </section>
      {batch && <section className="panel import-preview"><div className="section-head"><div><p className="panel-label">Предпросмотр</p><h2>{batch.valid_items} из {batch.total_items} строк готовы</h2></div></div><div className="import-results">{items.map((item) => <div className={`import-result ${item.status}`} key={String(item.id)}><strong>Строка {String(item.row_number)}</strong><span>{item.status === "valid" ? "Готова к импорту" : JSON.stringify(item.validation_errors)}</span></div>)}</div><button className="confirm-primary apply-import" type="button" disabled={busy || batch.valid_items !== batch.total_items} onClick={() => void apply()}>Импортировать</button></section>}
    </section>
  );
}
